// client/src/components/secure/training/MyTraining.js — the person's own training page:
// notices, Training of the week, what is assigned (with due dates), sessions, history.
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApiClient } from '../../../hooks/useApiClient';
import AssignmentPlayer from './AssignmentPlayer';
import { Btn, Notice, Pill, fmtDate, fmtDateTime } from './shared';
import DataList from '../../../components/shared/DataList';

const MyTraining = ({ onOpenLibrary }) => {
    const api = useApiClient();
    const [d, setD] = useState(null);
    const [error, setError] = useState('');
    const [open, setOpen] = useState(null);
    const [showDone, setShowDone] = useState(false);

    const load = useCallback(async () => {
        try { setD(await api.get('/api/training/me')); setError(''); } catch (e) { setError(e.message); }
    }, [api]);
    useEffect(() => { load(); }, [load]);

    const read = async (n) => { try { await api.post(`/api/training/notifications/${n.notification_id}/read`, {}); load(); } catch { /* ignore */ } };

    if (!d) return error ? <Notice tone="bad">{error}</Notice> : <p>Loading…</p>;
    const openItems = d.assignments.filter((a) => !['completed', 'waived'].includes(a.status));
    const doneItems = d.assignments.filter((a) => ['completed', 'waived'].includes(a.status));
    const unread = d.notifications.filter((n) => !n.read_at);
    const weeklyMine = d.weekly && d.assignments.find((a) => a.source === 'weekly' && a.period_key === d.weekly.week_key);

    return (
        <div className="space-y-5">
            {error && <Notice tone="bad">{error}</Notice>}
            {!d.me && <Notice tone="warn">Your login is not linked to an agent or employee record, so training cannot be recorded for it. {d.is_supervisor ? 'You can still run the administration tabs.' : 'Ask the office to link it.'}</Notice>}

            {unread.length > 0 && (
                <section className="bg-white rounded-lg shadow p-4">
                    <h3 className="font-semibold text-slate-800 mb-2">Notices ({unread.length})</h3>
                    <ul className="divide-y">
                        {unread.slice(0, 10).map((n) => (
                            <li key={n.notification_id} className="py-2 flex justify-between gap-3 text-sm">
                                <div><span className="font-medium">{n.title}</span>{n.body ? <span className="text-slate-600"> — {n.body}</span> : null}<div className="text-xs text-slate-400">{fmtDateTime(n.created_at)}{n.for_supervisors ? ' · for supervisors' : ''}</div></div>
                                <button type="button" className="text-xs text-indigo-700 underline whitespace-nowrap" onClick={() => read(n)}>Mark read</button>
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            <section className="bg-white rounded-lg shadow p-4">
                <h3 className="font-semibold text-slate-800 mb-1">Training of the week</h3>
                {d.weekly ? (
                    <div className="flex flex-wrap justify-between items-center gap-3">
                        <div><div className="font-medium">{d.weekly.title}</div><div className="text-sm text-slate-600">{d.weekly.summary}</div></div>
                        {weeklyMine && <Btn onClick={() => setOpen(weeklyMine.assignment_id)}>{weeklyMine.status === 'completed' ? 'Review' : 'Start'}</Btn>}
                    </div>
                ) : <p className="text-sm text-slate-600">{d.weekly_enabled ? 'This week\'s training has not been published yet.' : 'The weekly training is not switched on.'}</p>}
                {d.sessions.length > 0 && (
                    <div className="mt-3 text-sm">
                        <div className="font-medium text-slate-700">Upcoming live sessions</div>
                        <ul className="list-disc pl-5">
                            {d.sessions.map((s) => (
                                <li key={s.session_id}>{fmtDateTime(s.starts_at)} — {s.title}{s.meeting_url ? <> · <a href={s.meeting_url} target="_blank" rel="noopener noreferrer" className="text-indigo-700 underline">join link</a></> : ''}</li>
                            ))}
                        </ul>
                    </div>
                )}
            </section>

            <section className="bg-white rounded-lg shadow p-4">
                <div className="flex justify-between items-center mb-2">
                    <h3 className="font-semibold text-slate-800">Assigned to me ({openItems.length})</h3>
                    <Btn kind="ghost" onClick={onOpenLibrary}>Browse the library</Btn>
                </div>
                {!openItems.length && <p className="text-sm text-slate-600">Nothing outstanding. Well done.</p>}
                <ul className="divide-y">
                    {openItems.map((a) => (
                        <li key={a.assignment_id} className="py-2 flex flex-wrap justify-between gap-2 items-center">
                            <div>
                                <div className="font-medium">{a.course_title}</div>
                                <div className="text-xs text-slate-500">{a.source === 'weekly' ? 'Training of the week' : a.source === 'requirement' ? `Required (${a.req_key})` : a.source === 'monthly' ? 'Module of the month' : a.source === 'program' ? '90-day programme' : a.source === 'manual' ? 'Assigned by the office' : a.source} · about {a.estimated_minutes} min</div>
                                {a.assign_note && <div className="text-xs text-slate-600">Note: {a.assign_note}</div>}
                            </div>
                            <div className="flex gap-2 items-center">
                                {a.overdue ? <Pill s="overdue">overdue — due {fmtDate(a.due_date)}</Pill> : a.due_date ? <span className="text-sm text-slate-600">Due {fmtDate(a.due_date)}</span> : null}
                                <Pill s={a.status} />
                                <Btn onClick={() => setOpen(a.assignment_id)}>{a.status === 'assigned' ? 'Start' : 'Continue'}</Btn>
                            </div>
                        </li>
                    ))}
                </ul>
            </section>

            <section className="bg-white rounded-lg shadow p-4">
                <button type="button" className="font-semibold text-slate-800" onClick={() => setShowDone(!showDone)}>{showDone ? '▾' : '▸'} Completed ({doneItems.length})</button>
                {showDone && (
                    <div className="mt-2"><DataList rows={doneItems} rowKey="assignment_id" csvName="my-completed-training" defaultSort={{ key: 'completed_at', dir: 'desc' }}
                        columns={[
                            { key: 'course_title', label: 'Course' },
                            { key: 'completed_at', label: 'Completed', type: 'date', value: (a) => (a.status === 'waived' ? 'waived' : a.completed_at), sortValue: (a) => a.completed_at || null,
                                render: (a) => (a.status === 'waived' ? 'waived' : fmtDate(a.completed_at)) },
                            { key: 'completed_via', label: 'How', render: (a) => a.completed_via || '—' },
                            { key: 'quiz_score', label: 'Score', type: 'number', render: (a) => (a.quiz_score != null ? `${a.quiz_score}%` : '—') },
                            { key: 'actions', label: '', sortable: false, csv: false, search: false, render: (a) => <button type="button" className="text-indigo-700 underline" onClick={() => setOpen(a.assignment_id)}>View</button> },
                        ]} /></div>
                )}
                {d.me && <p className="text-xs text-slate-500 mt-2">Your printable training record: <Link className="text-indigo-700 underline" to={`/dashboard/training/record/${d.me.person_type}/${d.me.person_id}`}>open</Link>.</p>}
            </section>
            <p className="text-xs text-slate-500">{d.disclaimer}</p>
            {open && <AssignmentPlayer assignmentId={open} onClose={() => { setOpen(null); load(); }} onChanged={load} />}
        </div>
    );
};

export default MyTraining;
